import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import KitchenFields from '../components/KitchenFields';
import MusicOnlyToggle from '../components/MusicOnlyToggle';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Plus,
  X,
  Trash2,
  Phone,
  Mail,
  ArrowLeft,
  Edit3,
  Move,
  FileText,
  Printer,
  Copy,
  Save,
  CreditCard,
  Upload,
  Download,
  File,
  Link2,
  Unlink,
  Utensils,
} from 'lucide-react';
import { fmt, fmtDate, fmtDateLong, fmtDateRange, eventSpansDate, getEventDayCount } from '../lib/utils';
import { STAGE_LABELS, STAGE_COLOURS } from '../lib/sampleData';
import { uploadAttachment, deleteAttachment, getAttachmentUrl, fetchVenueBlocksByDates } from '../lib/supabaseService';
// Ad-hoc closures (Christmas, the January off-season, etc.) for the
// event/function-room venues — see lib/venueClosures.
import { getVenueClosureReason, dateRangeKeys } from '../lib/venueClosures';
import { toLocalDateKey, todayKey, parseLocalDate, addDays } from '../lib/dateHelpers';
import EventTasks from '../components/EventTasks';
import EventHistory from '../components/EventHistory';
import { FunctionSheetBody } from '../components/FunctionSheetRenderer';
import TimeSelect from '../components/TimeSelect';
import { useNotify } from '../components/NotifyProvider';
import { DetailStatusPanel, DetailStatusDot } from '../components/EventDetailStatus';
import { getDetailSaveBlock, missingFieldClass } from '../lib/eventCompleteness';
// Restaurant venue rules live in one place — see lib/restaurantConstants.
import {
  RESTAURANT_VENUES,
  RESTAURANT_SITTINGS,
  getSittingsForVenueAndDate,
  getMinCoversForVenue as getMinCoversForRestaurant,
  guessSittingFromTime,
} from '../lib/restaurantConstants';

const EVENT_TYPE_OPTIONS = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'private_party', label: 'Private Party' },
  { value: 'community', label: 'Community' },
  { value: 'charity', label: 'Charity' },
  { value: 'music_night', label: 'Music Night' },
  { value: 'restaurant_booking', label: 'Restaurant Booking' },
  { value: 'social', label: 'Social' },
  { value: 'other', label: 'Other' },
];

const EVENT_SUBTYPES = [
  'Birthday', 'Anniversary', 'Communion', 'Christening', 'Graduation',
  'Engagement', 'Baby Shower', 'Retirement', 'Reunion',
];

const SETUP_STYLES = [
  { value: 'theatre', label: 'Theatre' },
  { value: 'banquet', label: 'Banquet' },
  { value: 'classroom', label: 'Classroom' },
  { value: 'boardroom', label: 'Boardroom' },
  { value: 'u_shape', label: 'U-Shape' },
  { value: 'cabaret', label: 'Cabaret' },
  { value: 'reception', label: 'Reception' },
  { value: 'other', label: 'Other' },
];

const EQUIPMENT_OPTIONS = [
  'projector', 'sound_system', 'lighting', 'stage', 'dance_floor',
  'flipchart', 'whiteboard', 'screen', 'microphone', 'lectern',
];

const FB_PRESETS = [
  { value: 'morning_tea', label: 'Morning Tea' },
  { value: 'afternoon_tea', label: 'Afternoon Tea' },
  { value: 'tea_coffee', label: 'Tea & Coffee' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'welcome_cocktail', label: 'Welcome Cocktail' },
  { value: 'cocktail_hour', label: 'Cocktail Hour' },
  { value: 'canapes', label: 'Canapés' },
  { value: 'wine_selection', label: 'Wine Selection' },
  { value: 'cheese_board', label: 'Cheese Board' },
  { value: 'sandwiches', label: 'Sandwiches' },
  { value: 'cake', label: 'Cake' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'evening_snacks', label: 'Evening Snacks' },
  { value: 'welcome_drinks', label: 'Welcome Drinks' },
  { value: 'evening_reception', label: 'Evening Reception' },
  { value: 'after_party_room', label: 'After Party Room' },
  { value: 'bbq', label: 'BBQ' },
];

const PIPELINE_STAGES = ['enquiry', 'quote_sent', 'confirmed', 'deposit_paid', 'in_progress', 'completed', 'cancelled'];

function getEmptyAddFormData() {
  return {
    eventName: '',
    clientName: '',
    contactPhone: '',
    contactEmail: '',
    type: 'private_party',
    subtype: '',
    endDate: '',
    guests: 0,
    // Blank on purpose (Oct 2026) — defaults made bookings look complete.
    startTime: '',
    endTime: '',
    setup: '',
    setupRequirements: '',
    menuNotes: '',
    dietaryRequirements: '',
    dietaryVeg: '',
    dietaryVegan: '',
    dietaryCoeliac: '',
    dietaryAllergens: '',
    dietaryNA: false,
    musicOnly: false,
    foodServiceTime: '',
    equipment: [],
    fbItems: [],
    services: [{ time: '', description: '', location: '' }],
    roomHire: 0,
    pricePerHead: 0,
    packageTotal: 0,
    billingType: 'group',
    stage: 'enquiry',
    notes: '',
  };
}

export default function RoomDiary({ onNavigate, onSelectEvent, navigateAction, clearNavigateAction }) {
  const { events, payments, venues, clients, tableBookings, addEvent, deleteEvent, updateEvent, addClient, addPayment, deletePayment, unlinkBookingFromEvent, eventTasks, addTableBooking, linkBookingToEvent, deleteTableBooking } = useAppContext();
  const { notifySuccess, notifyError } = useNotify();

  // Function rooms + event spaces always show. Restaurant/bar areas (R&R,
  // Brewery Bar, Reynards) only get a row when an event is actually held
  // there in the period on screen — e.g. a table booking upgraded to an event
  // that stays in R&R. Without this those events were invisible in the diary.
  const FUNCTION_VENUE_TYPES = ['function_room', 'event_space'];
  const venuesForRange = (startKey, endKey) => venues.filter((v) =>
    FUNCTION_VENUE_TYPES.includes(v.venue_type) ||
    events.some((e) => e.venue_id === v.id &&
      !['archived', 'lost', 'cancelled'].includes(e.stage) &&
      e.date && e.date <= endKey && (e.endDate || e.date) >= startKey));
  // Forms list every venue so an event in R&R can be edited (and moved) too.
  const venuesForForms = [
    ...venues.filter((v) => FUNCTION_VENUE_TYPES.includes(v.venue_type)),
    ...venues.filter((v) => !FUNCTION_VENUE_TYPES.includes(v.venue_type)),
  ];
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailySheetDate, setDailySheetDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Deep link from other pages (e.g. Tables "View in Room Diary" on a booking
  // upgraded to an event): jump to that event's week and open it.
  useEffect(() => {
    const id = navigateAction && navigateAction.scrollToEvent;
    if (!id) return;
    const evt = events.find((e) => e.id === id);
    if (evt) {
      const d = parseLocalDate(evt.date);
      if (d) setCurrentDate(d);
      setViewMode('weekly');
      handleOpenEditModal(evt);
    }
    if (typeof clearNavigateAction === 'function') clearNavigateAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateAction, events]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Send-to-Restaurant state
  const [showSendToRestaurant, setShowSendToRestaurant] = useState(false);
  const [sendToRestaurantEvent, setSendToRestaurantEvent] = useState(null);
  const [sendToRestaurantData, setSendToRestaurantData] = useState({ venue: 'reynards', sitting: 'evening', time: '19:00', covers: '' });
  const [sendToRestaurantBusy, setSendToRestaurantBusy] = useState(false);

  // ─── Send event to a restaurant table ───
  const getLinkedTableBookingForEvent = (eventId) => (tableBookings || []).find((b) => b.linkedEventId === eventId && b.source === 'event');

  const canSendEventToRestaurant = (event) => {
    if (!event) return false;
    if (event.type?.toLowerCase() === 'wedding') return false;
    const guests = parseInt(event.guests) || 0;
    if (guests > 50) return false;
    if (getLinkedTableBookingForEvent(event.id)) return false;
    return true;
  };

  const handleOpenSendToRestaurant = (event) => {
    const guessed = guessSittingFromTime(event.startTime) || RESTAURANT_SITTINGS[2];
    const guests = event.guests || 2;
    // Reynards requires 6+ every day — default to a venue valid for this
    // party size so staff don't have to immediately change the dropdown.
    const defaultVenue = guests >= 6 ? 'reynards' : 'randr';
    setSendToRestaurantEvent(event);
    setSendToRestaurantData({
      venue: defaultVenue,
      sitting: guessed.id,
      time: event.startTime || `${String(guessed.startH).padStart(2, '0')}:00`,
      covers: String(guests),
    });
    setShowSendToRestaurant(true);
  };

  const handleCloseSendToRestaurant = () => {
    setShowSendToRestaurant(false);
    setSendToRestaurantEvent(null);
    setSendToRestaurantBusy(false);
  };

  const handleConfirmSendToRestaurant = async () => {
    const event = sendToRestaurantEvent;
    if (!event) return;
    const { venue, sitting, time, covers } = sendToRestaurantData;
    const coversNum = parseInt(covers);

    if (!venue) { notifyError('Please pick a restaurant.'); return; }
    if (!sitting) { notifyError('Please pick a sitting.'); return; }
    if (!time) { notifyError('Please set a time.'); return; }
    if (!coversNum || coversNum < 1) { notifyError('Covers must be at least 1.'); return; }

    const sittingDef = RESTAURANT_SITTINGS.find((s) => s.id === sitting);
    const validSittings = getSittingsForVenueAndDate(venue, event.date);
    if (!validSittings.some((s) => s.id === sitting)) {
      const vName = RESTAURANT_VENUES.find((v) => v.id === venue)?.name || venue;
      notifyError(`${vName} is not open for ${sitting} on ${event.date}.`);
      return;
    }
    if (sittingDef) {
      const [h, m] = time.split(':').map(Number);
      const mins = h * 60 + (m || 0);
      if (mins < sittingDef.startH * 60 || mins >= sittingDef.endH * 60) {
        notifyError(`Time ${time} is outside the ${sittingDef.label} sitting (${sittingDef.time}).`);
        return;
      }
    }
    const cap = RESTAURANT_VENUES.find((v) => v.id === venue)?.capacity || 60;
    if (coversNum > cap) {
      notifyError(`${RESTAURANT_VENUES.find((v) => v.id === venue)?.name} seats a max of ${cap}.`);
      return;
    }
    const minCovers = getMinCoversForRestaurant(venue);
    if (coversNum < minCovers) {
      notifyError(`Reynards requires a minimum of ${minCovers} covers.`);
      return;
    }

    setSendToRestaurantBusy(true);
    try {
      const bookingData = {
        id: `booking-${Date.now()}`,
        guestName: event.client || event.name,
        phone: event.clientPhone || '',
        email: event.clientEmail || '',
        venue,
        date: event.date,
        time,
        covers: coversNum,
        source: 'event',
        roomNumber: '',
        status: 'booked',
        tableNumber: '',
        notes: `From event: ${event.name}${event.notes ? `\n${event.notes}` : ''}`,
        linkedEventId: event.id,
        eventPreviousVenueId: event.venue_id || null,
      };
      const newBooking = await addTableBooking(bookingData);
      if (newBooking?._dbError) {
        notifyError(`Saved locally but database save failed: ${newBooking._dbError}.`);
        setSendToRestaurantBusy(false);
        return;
      }
      // Release the event's venue so it leaves Room Diary.
      await updateEvent(event.id, { venue_id: null });
      const vName = RESTAURANT_VENUES.find((v) => v.id === venue)?.name || venue;
      notifySuccess(`Sent to ${vName} (${sittingDef?.label || sitting} sitting, ${coversNum} covers)`);
      handleCloseSendToRestaurant();
      // Close the detail modal too since the event has left this venue.
      setSelectedEvent(null);
    } catch (err) {
      console.error('[RoomDiary] Send to restaurant failed:', err);
      notifyError('Failed to send to restaurant: ' + err.message);
      setSendToRestaurantBusy(false);
    }
  };

  const handleUnlinkFromRestaurant = async (event) => {
    const booking = getLinkedTableBookingForEvent(event.id);
    if (!booking) return;
    const previousVenueId = booking.eventPreviousVenueId || null;
    const restoreVenue = (venues || []).find((v) => v.id === previousVenueId);
    const restoreMsg = restoreVenue
      ? `\n\nThe event will return to "${restoreVenue.name}" in Room Diary.`
      : `\n\nThe event will return to the Events list (no venue assigned).`;
    if (!window.confirm(`Remove the linked table booking at ${RESTAURANT_VENUES.find((v) => v.id === booking.venue)?.name || booking.venue}?${restoreMsg}`)) return;
    try {
      await deleteTableBooking(booking.id);
      if (previousVenueId) {
        await updateEvent(event.id, { venue_id: previousVenueId });
      }
    } catch (err) {
      console.error('[RoomDiary] Unlink from restaurant failed:', err);
      notifyError('Failed to remove table booking: ' + err.message);
    }
  };

  const [addFormData, setAddFormData] = useState({
    date: null,
    venueId: null,
  });
  const [cloneSourceEvent, setCloneSourceEvent] = useState(null);

  // Unified form state – used for both add and edit modes
  const [addFormFields, setAddFormFields] = useState(getEmptyAddFormData());
  const [editingEventId, setEditingEventId] = useState(null); // null = add mode, string = edit mode

  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '', payment_date: todayKey(),
    paymentType: 'bank_transfer', category: 'fb_package', description: '',
  });

  // Attachment state
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);

  // Client autocomplete state
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // Close modals on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedEvent(null);
        setShowAddForm(false);
        setShowEditForm(false);
        setShowDeleteConfirm(false);
        setCloneSourceEvent(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Get the start of the current week (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  // Get the start of the current month
  const getMonthStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Navigation handlers
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Always step from the 1st so e.g. 31 Jan + 1 month doesn't overflow to March.
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Get days of week for weekly view
  const getDaysOfWeek = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Check if Full Skeffington is booked on a given date and return blocked suites
  const getBlockedSuites = (date) => {
    const dateStr = toLocalDateKey(date);
    const fullSkeffBooking = events.find(
      (evt) => evt.venue_id === 'skeff-full' && eventSpansDate(evt, dateStr)
    );
    return fullSkeffBooking ? ['skeff1', 'skeff2'] : [];
  };

  // Filter events for a specific date and venue (supports multi-day events)
  const getEventsForDateAndVenue = (date, venueId) => {
    const dateStr = toLocalDateKey(date);
    return events.filter((evt) => eventSpansDate(evt, dateStr) && evt.venue_id === venueId);
  };

  // Client autocomplete handlers
  function handleClientNameChange(e, formDataSetter) {
    const value = e.target.value;
    formDataSetter((prev) => ({ ...prev, client: value }));

    if (value.length >= 2 && clients) {
      const matches = clients.filter((c) =>
        c.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setClientSuggestions(matches);
      setShowClientSuggestions(matches.length > 0);
    } else {
      setShowClientSuggestions(false);
    }
  }

  function handleSelectClient(client, formDataSetter) {
    formDataSetter((prev) => ({
      ...prev,
      client: client.name,
      clientPhone: client.phone || prev.clientPhone,
      clientEmail: client.email || prev.clientEmail,
    }));
    setShowClientSuggestions(false);
  }

  // Calculate summaries for weekly/monthly view
  const getSummary = useMemo(() => {
    if (viewMode === 'weekly') {
      const weekStart = getWeekStart(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekStartStr = toLocalDateKey(weekStart);
      const weekEndStr = toLocalDateKey(weekEnd);
      const weekEvents = events.filter((evt) => {
        const evtEnd = evt.endDate || evt.date;
        // Event overlaps the week if it starts before week ends AND ends after week starts
        return evt.date <= weekEndStr && evtEnd >= weekStartStr;
      });

      const venuesUsed = new Set(weekEvents.map((evt) => evt.venue_id)).size;
      const revenue = weekEvents.reduce(
        (sum, evt) => sum + (evt.packageTotal || 0) + (evt.consumption || 0),
        0
      );

      return {
        count: weekEvents.length,
        venuesUsed,
        revenue,
      };
    } else {
      const monthStart = getMonthStart(currentDate);
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0);

      const monthStartStr = toLocalDateKey(monthStart);
      const monthEndStr = toLocalDateKey(monthEnd);
      const monthEvents = events.filter((evt) => {
        const evtEnd = evt.endDate || evt.date;
        return evt.date <= monthEndStr && evtEnd >= monthStartStr;
      });

      const venuesUsed = new Set(monthEvents.map((evt) => evt.venue_id)).size;
      const revenue = monthEvents.reduce(
        (sum, evt) => sum + (evt.packageTotal || 0) + (evt.consumption || 0),
        0
      );

      return {
        count: monthEvents.length,
        venuesUsed,
        revenue,
      };
    }
  }, [currentDate, viewMode, events]);

  const summary = getSummary;

  // Venue closures (hotel shut for Christmas, the January off-season, etc.)
  // visible on the calendar. Re-fetched whenever the visible range changes
  // (switching week/month/year, or navigating to a different one) so the
  // grid always reflects the latest closures — same data the Add/Edit Event
  // save guards check, just also surfaced here so staff see it before they
  // even try to book.
  const [closureBlocks, setClosureBlocks] = useState([]);
  useEffect(() => {
    let cancelled = false;
    async function loadClosures() {
      let start, end;
      if (viewMode === 'weekly') {
        start = getWeekStart(currentDate);
        end = addDays(start, 6);
      } else if (viewMode === 'monthly') {
        start = getMonthStart(currentDate);
        end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
      } else {
        // annual
        start = new Date(currentDate.getFullYear(), 0, 1);
        end = new Date(currentDate.getFullYear(), 11, 31);
      }
      const dates = dateRangeKeys(toLocalDateKey(start), toLocalDateKey(end));
      try {
        const blocks = await fetchVenueBlocksByDates(dates);
        if (!cancelled) setClosureBlocks(blocks);
      } catch (err) {
        console.error('[RoomDiary] Could not load venue closures:', err);
        if (!cancelled) setClosureBlocks([]);
      }
    }
    loadClosures();
    return () => { cancelled = true; };
  }, [currentDate, viewMode]);

  const formatMonthDisplay = (date) => {
    return date.toLocaleDateString('en-IE', { month: 'long', year: 'numeric' });
  };

  // Weekly View Component
  const WeeklyView = () => {
    const weekStart = getWeekStart(currentDate);
    const daysOfWeek = getDaysOfWeek(weekStart);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Function rooms and event spaces, plus any restaurant area with an event this week
    const displayVenues = venuesForRange(toLocalDateKey(daysOfWeek[0]), toLocalDateKey(daysOfWeek[daysOfWeek.length - 1]));

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Calendar Grid */}
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                <th className="p-2 md:p-3 text-left font-semibold text-xs md:text-sm text-slate-700 w-32 md:w-48 sticky left-0 bg-slate-100 z-10">
                  Venue
                </th>
                {daysOfWeek.map((date) => {
                  const isToday =
                    date.toDateString() === new Date().toDateString();
                  const dayName = date.toLocaleDateString('en-IE', {
                    weekday: 'short',
                  });
                  const dayNum = date.getDate();

                  return (
                    <th
                      key={date.toISOString()}
                      className={`p-2 md:p-3 text-center font-semibold text-xs md:text-sm border-l border-slate-200 min-w-[100px] md:min-w-[140px] lg:min-w-[160px] ${
                        isToday ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="font-bold text-slate-800 md:text-slate-900">{dayName}</div>
                      <div className="text-slate-600 text-xs md:text-sm">{dayNum}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayVenues.map((venue) => {
                const isBlocked = getBlockedSuites(new Date()).includes(venue.id);

                return (
                  <tr key={venue.id} className="border-b border-slate-200">
                    <td className="p-2 md:p-3 font-medium text-xs md:text-sm text-slate-900 sticky left-0 bg-white border-r border-slate-200 z-10">
                      {venue.name}
                      {isBlocked && (
                        <div className="text-xs text-red-600 font-semibold mt-1">
                          BLOCKED
                        </div>
                      )}
                    </td>
                    {daysOfWeek.map((date) => {
                      const dateStr = toLocalDateKey(date);
                      const isToday =
                        date.toDateString() === new Date().toDateString();
                      const blockedSuites = getBlockedSuites(date);
                      const isVenueBlocked = blockedSuites.includes(venue.id);
                      const venueeEvents = getEventsForDateAndVenue(date, venue.id);
                      const closureReason = getVenueClosureReason(venue.id, dateStr, null, closureBlocks);

                      return (
                        <td
                          key={`${venue.id}-${dateStr}`}
                          className={`p-1 md:p-2 border-l border-slate-200 min-w-[100px] md:min-w-[140px] lg:min-w-[160px] align-top relative group ${
                            isToday ? 'bg-blue-50' : ''
                          } ${isVenueBlocked ? 'bg-slate-100' : ''} ${closureReason && !isVenueBlocked ? 'bg-red-50' : ''}`}
                        >
                          {isVenueBlocked ? (
                            <div className="text-center text-slate-400 text-xs font-semibold py-4 md:py-8">
                              Blocked (Full Suite)
                            </div>
                          ) : (
                            <>
                              <div className="space-y-0.5 md:space-y-1">
                                {venueeEvents.map((evt) => (
                                  <EventBlock key={evt.id} event={evt} dateStr={dateStr} />
                                ))}
                              </div>
                              {closureReason ? (
                                <div
                                  className="text-center text-red-600 text-xs font-semibold py-2 md:py-4"
                                  title={closureReason}
                                >
                                  Closed
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setAddFormData({
                                      date,
                                      venueId: venue.id,
                                    });
                                    setShowAddForm(true);
                                  }}
                                  className="mt-1 md:mt-2 w-full p-1 min-h-[44px] md:min-h-auto rounded border-2 border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1 text-xs opacity-0 group-hover:opacity-100"
                                  title="Add event"
                                >
                                  <Plus size={12} className="md:size-4" />
                                  <span className="hidden md:inline">Add</span>
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Monthly View Component
  const MonthlyView = () => {
    const monthStart = getMonthStart(currentDate);
    const monthEnd = new Date(monthStart);
    monthEnd.setMonth(monthEnd.getMonth() + 1);
    monthEnd.setDate(0);
    const displayVenues = venuesForRange(toLocalDateKey(monthStart), toLocalDateKey(monthEnd));

    // Grid is Monday-first (header below runs Mon..Sun), so convert JS
    // getDay() (0=Sun) to a Monday-based offset. Using getDay() raw put
    // every date one column to the right (e.g. Fri 1 Jan 2027 under Sat).
    const firstDay = (monthStart.getDay() + 6) % 7;
    const daysInMonth = monthEnd.getDate();
    const weeks = [];

    // Create weeks array
    let currentWeek = [];
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(null); // Empty cells for days before month starts
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(monthStart);
      date.setDate(day);
      currentWeek.push(date);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Fill remaining cells
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push(null);
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-300">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <th
                  key={day}
                  className="p-2 md:p-3 text-center font-semibold text-xs md:text-sm text-slate-700"
                >
                  <span className="hidden md:inline">{day}</span>
                  <span className="md:hidden text-xs font-bold">{day.charAt(0)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIdx) => (
              <tr key={weekIdx} className="border-b border-slate-200">
                {week.map((date, dayIdx) => {
                  const isCurrentMonth = date !== null;
                  const isToday =
                    date && date.toDateString() === today.toDateString();
                  const dayEvents = date
                    ? events.filter(
                        (evt) =>
                          eventSpansDate(evt, toLocalDateKey(date))
                      )
                    : [];
                  // A day counts as "hotel closed" when every function/event
                  // venue is closed that date (the case for a full hotel
                  // shutdown like Christmas or the January off-season). A
                  // partial closure (just one room out for maintenance, say)
                  // shows a smaller note instead of hiding the Add button.
                  const dateKeyForClosure = date ? toLocalDateKey(date) : null;
                  const closedVenueCount = dateKeyForClosure
                    ? displayVenues.filter((v) => getVenueClosureReason(v.id, dateKeyForClosure, null, closureBlocks)).length
                    : 0;
                  const isFullyClosed = isCurrentMonth && displayVenues.length > 0 && closedVenueCount === displayVenues.length;
                  const isPartiallyClosed = isCurrentMonth && closedVenueCount > 0 && !isFullyClosed;

                  return (
                    <td
                      key={dayIdx}
                      className={`p-2 md:p-3 min-h-[80px] md:min-h-[120px] border-r border-slate-200 align-top relative group text-xs md:text-sm ${
                        !isCurrentMonth ? 'bg-slate-50' : ''
                      } ${isToday ? 'bg-blue-50 border-2 border-blue-300' : ''} ${isFullyClosed ? 'bg-red-50' : ''}`}
                    >
                      <div className="font-semibold text-slate-900 mb-1 md:mb-2 flex items-center justify-between">
                        <span className="text-xs md:text-base">{date ? date.getDate() : ''}</span>
                        {isCurrentMonth && !isFullyClosed && (
                          <button
                            onClick={() => {
                              setAddFormData({
                                date,
                                venueId: venues.find(
                                  (v) =>
                                    v.venue_type === 'function_room' ||
                                    v.venue_type === 'event_space'
                                )?.id,
                              });
                              setShowAddForm(true);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 min-h-[32px] min-w-[32px] md:min-h-auto md:min-w-auto hover:bg-blue-100 rounded transition-all flex items-center justify-center"
                            title="Add event"
                          >
                            <Plus size={14} className="md:size-4 text-blue-600" />
                          </button>
                        )}
                      </div>
                      {isFullyClosed && (
                        <div className="text-red-600 text-[10px] md:text-xs font-semibold mb-1">
                          Hotel Closed
                        </div>
                      )}
                      {isPartiallyClosed && (
                        <div className="text-red-500 text-[9px] md:text-xs font-medium mb-1">
                          {closedVenueCount} venue{closedVenueCount > 1 ? 's' : ''} closed
                        </div>
                      )}
                      <div className="space-y-0.5 md:space-y-1">
                        {dayEvents.slice(0, 3).map((evt) => {
                          const venue = venues.find((v) => v.id === evt.venue_id);
                          const venueName = venue?.name?.replace('The Full Skeffington Suite', 'Full Skeff').replace('The Annalee Room', 'Annalee').replace('The Corrick Suite', 'Corrick') || '';
                          return (
                            <div
                              key={evt.id}
                              onClick={() => handleOpenEditModal(evt)}
                              className="text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded text-white cursor-pointer hover:opacity-80 transition-opacity"
                              style={{
                                backgroundColor: venue?.colour || '#999999',
                              }}
                              title={`${evt.name} — ${venue?.name || 'No venue'} · ${evt.guests || 0} guests${evt.startTime ? ' · ' + evt.startTime : ''}`}
                            >
                              <span className="flex items-center gap-1 truncate"><DetailStatusDot event={evt} /><span className="truncate">{evt.name}</span></span>
                              <span className="block truncate text-[9px] opacity-80 hidden md:block">{venueName}</span>
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-slate-500 px-1.5 md:px-2">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Venue Colour Legend */}
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-slate-50 border-t border-slate-200">
          {displayVenues.map((v) => (
            <span key={v.id} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-3 h-3 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: v.colour }} />
              {v.name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Annual View – 12 mini-month calendars for the year
  const AnnualView = () => {
    const year = currentDate.getFullYear();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    const displayVenues = venuesForRange(`${year}-01-01`, `${year}-12-31`);

    // Pre-compute which dates in the year are fully hotel-closed (every
    // function/event venue closed that day — a full shutdown like Christmas
    // or the January off-season) so each mini-month day cell can flag it
    // at a glance, same idea as eventsByDate below.
    const closedDatesSet = new Set();
    if (displayVenues.length > 0) {
      const dateCounts = {};
      closureBlocks.forEach((b) => {
        if (b.sittingId !== 'all_day' || b.blockType !== 'closed') return;
        if (!displayVenues.some((v) => v.id === b.venueId)) return;
        dateCounts[b.date] = (dateCounts[b.date] || 0) + 1;
      });
      Object.entries(dateCounts).forEach(([date, count]) => {
        if (count >= displayVenues.length) closedDatesSet.add(date);
      });
    }

    // Pre-compute events per date for the whole year (names + count for tooltips)
    // Supports multi-day events by expanding them across their date range
    const eventsByDate = {};
    events.forEach((evt) => {
      if (!evt.date) return;
      const startStr = evt.date;
      const endStr = evt.endDate || evt.date;
      // Only process events that overlap this year
      if (startStr > `${year}-12-31` || endStr < `${year}-01-01`) return;
      const loopStart = startStr < `${year}-01-01` ? `${year}-01-01` : startStr;
      const loopEnd = endStr > `${year}-12-31` ? `${year}-12-31` : endStr;
      let cur = new Date(loopStart + 'T12:00:00');
      const end = new Date(loopEnd + 'T12:00:00');
      while (cur <= end) {
        const dk = toLocalDateKey(cur);
        if (!eventsByDate[dk]) eventsByDate[dk] = [];
        eventsByDate[dk].push(evt);
        cur.setDate(cur.getDate() + 1);
      }
    });

    const renderMiniMonth = (monthIdx) => {
      const monthStart = new Date(year, monthIdx, 1);
      const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
      const firstDay = (monthStart.getDay() + 6) % 7; // Monday-first, matches monthly view + Dashboard

      const weeks = [];
      let currentWeek = [];
      for (let i = 0; i < firstDay; i++) currentWeek.push(null);
      for (let day = 1; day <= daysInMonth; day++) {
        currentWeek.push(day);
        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }
      while (currentWeek.length > 0 && currentWeek.length < 7) currentWeek.push(null);
      if (currentWeek.length > 0) weeks.push(currentWeek);

      return (
        <div
          key={monthIdx}
          className="bg-white rounded-lg shadow p-3 cursor-pointer hover:ring-2 hover:ring-emerald-300 transition"
          onClick={() => {
            setCurrentDate(new Date(year, monthIdx, 1));
            setViewMode('monthly');
          }}
        >
          <h4 className="text-sm font-bold text-slate-800 mb-2 text-center">{monthNames[monthIdx]}</h4>
          <table className="w-full text-center">
            <thead>
              <tr>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <th key={i} className="text-[9px] font-medium text-slate-400 pb-1">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((day, di) => {
                    if (!day) return <td key={di} className="p-0.5" />;
                    const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayEvts = eventsByDate[dateStr] || [];
                    const count = dayEvts.length;
                    const isToday = new Date(year, monthIdx, day).toDateString() === today.toDateString();
                    const isClosed = closedDatesSet.has(dateStr);
                    const tooltip = (isClosed ? 'Hotel closed\n' : '') + (count > 0
                      ? dayEvts.slice(0, 5).map(e => {
                          const v = venues.find(vn => vn.id === e.venue_id);
                          return `${e.name} (${v?.name || '?'})`;
                        }).join('\n') + (count > 5 ? `\n+${count - 5} more` : '')
                      : '');
                    return (
                      <td key={di} className="p-0.5">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentDate(new Date(year, monthIdx, day));
                            setViewMode('weekly');
                          }}
                          className={`w-6 h-6 mx-auto flex items-center justify-center rounded-full text-[10px] font-medium transition cursor-pointer hover:ring-2 hover:ring-emerald-300
                            ${isToday && !isClosed ? 'ring-2 ring-blue-400 bg-blue-50 text-blue-700' : ''}
                            ${isClosed ? 'bg-red-100 text-red-700 font-bold ring-1 ring-red-300' : ''}
                            ${count > 0 && !isToday && !isClosed ? 'bg-emerald-100 text-emerald-800 font-bold' : ''}
                            ${count === 0 && !isToday && !isClosed ? 'text-slate-600 hover:bg-slate-100' : ''}
                          `}
                          title={tooltip}
                        >
                          {day}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }, (_, i) => renderMiniMonth(i))}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-slate-500 justify-center">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-300 inline-block" /> Has events</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-50 ring-2 ring-blue-400 inline-block" /> Today</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-100 ring-1 ring-red-300 inline-block" /> Hotel closed</span>
          <span className="text-slate-400">Click any day to view that week · Hover for event details</span>
        </div>
      </div>
    );
  };

  // Event Block for weekly view
  const EventBlock = ({ event, dateStr }) => {
    const venue = venues.find((v) => v.id === event.venue_id);
    const truncatedName = event.name.substring(0, 15);
    const dayCount = getEventDayCount(event);
    const isMultiDay = dayCount > 1;
    // Determine position in span: is this the first day, middle, or last?
    const isFirstDay = !dateStr || dateStr === event.date;
    const isLastDay = !dateStr || dateStr === (event.endDate || event.date);

    return (
      <div
        className={`p-1 md:p-2 text-white text-xs cursor-pointer hover:opacity-90 transition-opacity shadow-sm relative group ${
          isMultiDay
            ? `${isFirstDay ? 'rounded-l' : 'rounded-l-none'} ${isLastDay ? 'rounded-r' : 'rounded-r-none'}`
            : 'rounded'
        }`}
        style={{
          backgroundColor: venue?.colour || '#gray',
          ...(isMultiDay && !isFirstDay ? { borderLeft: '2px solid rgba(255,255,255,0.3)' } : {}),
        }}
        title={`${event.name}${isMultiDay ? ` (${dayCount}-day event: ${fmtDateRange(event.date, event.endDate)})` : ''}`}
        onClick={() => handleOpenEditModal(event)}
      >
        <div className="font-semibold truncate text-xs md:text-xs flex items-center gap-1">{isFirstDay && <DetailStatusDot event={event} />}<span className="truncate">{truncatedName}</span></div>
        {isFirstDay && <div className="text-xs opacity-90 leading-tight">{event.startTime}</div>}
        {!isFirstDay && isMultiDay && <div className="text-xs opacity-70 leading-tight italic">cont'd</div>}
        <div className="text-xs opacity-90 flex items-center gap-0.5 leading-tight">
          <Users size={10} className="md:size-3" />
          <span className="hidden md:inline">{event.guests}</span>
          {isMultiDay && isFirstDay && <span className="hidden md:inline ml-1 opacity-70">({dayCount}d)</span>}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (
              window.confirm(
                `Are you sure you want to delete "${event.name}"?`
              )
            ) {
              deleteEvent(event.id);
            }
          }}
          className="absolute top-0.5 right-0.5 md:top-1 md:right-1 opacity-0 group-hover:opacity-100 transition-opacity min-h-[32px] min-w-[32px] flex items-center justify-center md:min-h-auto md:min-w-auto"
          title="Delete event"
        >
          <Trash2 size={10} className="md:size-3 text-white drop-shadow" />
        </button>
      </div>
    );
  };

  // Event Detail View Component (legacy — kept for reference, no longer rendered)
  // eslint-disable-next-line no-unused-vars
  const EventDetailView = () => {
    if (!selectedEvent) return null;

    const venue = venues.find((v) => v.id === selectedEvent.venue_id);

    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 md:p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setSelectedEvent(null);
          }
        }}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header with Back Button */}
          <div className="sticky top-0 bg-white border-b border-slate-200 p-3 md:p-6 flex items-center gap-2 md:gap-4">
            <button
              onClick={() => setSelectedEvent(null)}
              className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
              title="Back to Room Diary"
            >
              <ArrowLeft size={18} className="md:size-5 text-slate-600" />
            </button>
            <h2 className="text-lg md:text-2xl font-bold text-slate-900 flex-1 truncate">
              {selectedEvent.name}
            </h2>
            <button
              onClick={() => setSelectedEvent(null)}
              className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
            >
              <X size={18} className="md:size-5 text-slate-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 md:p-6 space-y-4 md:space-y-6">
            {/* Event Type & Stage */}
            <div className="flex gap-2 md:gap-3 flex-wrap">
              <span className="px-2 md:px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium">
                {selectedEvent.type || 'Event'}
              </span>
              {selectedEvent.stage && (
                <span
                  className="px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium text-white"
                  style={{
                    backgroundColor:
                      STAGE_COLOURS[selectedEvent.stage] || '#999999',
                  }}
                >
                  {STAGE_LABELS[selectedEvent.stage] || selectedEvent.stage}
                </span>
              )}
            </div>

            {/* Client Information */}
            <div className="bg-slate-50 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
              <h3 className="font-semibold text-sm md:text-base text-slate-900">Client</h3>
              <div className="space-y-1 md:space-y-2">
                <div className="text-slate-900 text-sm md:text-base">
                  <strong>{selectedEvent.client || selectedEvent.clientName}</strong>
                </div>
                {selectedEvent.clientPhone && (
                  <div>
                    <a
                      href={`tel:${selectedEvent.clientPhone}`}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs md:text-sm"
                    >
                      <Phone size={14} className="md:size-4" />
                      {selectedEvent.clientPhone}
                    </a>
                  </div>
                )}
                {selectedEvent.clientEmail && (
                  <div>
                    <a
                      href={`mailto:${selectedEvent.clientEmail}`}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs md:text-sm break-all"
                    >
                      <Mail size={14} className="md:size-4 flex-shrink-0" />
                      {selectedEvent.clientEmail}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Event Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="text-xs md:text-sm font-semibold text-slate-600">
                  Date
                </label>
                <p className="text-slate-900">
                  {selectedEvent.endDate && selectedEvent.endDate !== selectedEvent.date
                    ? `${fmtDateLong(selectedEvent.date)} – ${fmtDateLong(selectedEvent.endDate)}`
                    : fmtDateLong(selectedEvent.date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Time
                </label>
                <p className="text-slate-900">
                  {selectedEvent.startTime}
                  {selectedEvent.endTime && ` - ${selectedEvent.endTime}`}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Venue
                </label>
                <p className="text-slate-900">{venue?.name}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Guest Count
                </label>
                <p className="text-slate-900">{selectedEvent.guests}</p>
              </div>
            </div>

            {/* Setup Description */}
            {selectedEvent.setupDescription && (
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Setup Description
                </label>
                <p className="text-slate-900 mt-1">
                  {selectedEvent.setupDescription}
                </p>
              </div>
            )}

            {/* Equipment List */}
            {selectedEvent.equipmentList &&
              selectedEvent.equipmentList.length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-slate-600">
                    Equipment
                  </label>
                  <ul className="list-disc list-inside text-slate-900 mt-1">
                    {selectedEvent.equipmentList.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

            {/* F&B Items */}
            {selectedEvent.fbItems?.length > 0 && (
              <div>
                <label className="text-sm font-semibold text-slate-600 block mb-2">
                  Food & Beverage
                </label>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-slate-600">Item</th>
                      <th className="text-right py-2 text-slate-600">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEvent.fbItems.map((item, idx) => {
                      const isObj = typeof item === 'object' && item !== null;
                      const name = isObj ? item.name : String(item).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      const price = isObj ? item.price : null;
                      return (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 text-slate-900">{name}</td>
                          <td className="text-right py-2 text-slate-900">
                            {price != null ? fmt(price) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Billing Information */}
            <div className="border-t border-slate-200 pt-4 space-y-2">
              {selectedEvent.roomHire && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Room Hire:</span>
                  <span className="font-medium text-slate-900">
                    {fmt(selectedEvent.roomHire)}
                  </span>
                </div>
              )}
              {selectedEvent.packageTotal && (
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total:</span>
                  <span className="text-slate-900">
                    {fmt(selectedEvent.packageTotal)}
                  </span>
                </div>
              )}
            </div>

            {/* Notes */}
            {selectedEvent.notes && (
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Notes
                </label>
                <p className="text-slate-900 mt-1 whitespace-pre-wrap">
                  {selectedEvent.notes}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="border-t border-slate-200 pt-6 space-y-3">
              <button
                onClick={() => handlePrintEventSheet(selectedEvent.id)}
                className="w-full px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg font-medium hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2"
              >
                <Printer size={18} />
                Print Function Sheet
              </button>
              <button
                onClick={() => onSelectEvent?.(selectedEvent)}
                className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
              >
                <FileText size={18} />
                View Full Details
              </button>
              {canSendEventToRestaurant(selectedEvent) && (
                <button
                  onClick={() => handleOpenSendToRestaurant(selectedEvent)}
                  className="w-full px-4 py-2 bg-purple-50 text-purple-700 rounded-lg font-medium hover:bg-purple-100 transition-colors flex items-center justify-center gap-2"
                  title="Create a matching table booking and release this room"
                >
                  <Utensils size={18} />
                  Send to Restaurant
                </button>
              )}
              {getLinkedTableBookingForEvent(selectedEvent.id) && (
                <button
                  onClick={() => { handleUnlinkFromRestaurant(selectedEvent); setSelectedEvent(null); }}
                  className="w-full px-4 py-2 bg-amber-50 text-amber-700 rounded-lg font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
                  title="Remove the linked table booking and restore the event's room"
                >
                  <Unlink size={18} />
                  Remove Restaurant Booking
                </button>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCloneSourceEvent(selectedEvent);
                    setSelectedEvent(null);
                    setAddFormData({
                      date: null,
                      venueId: null,
                    });
                    setShowAddForm(true);
                  }}
                  className="flex-1 px-4 py-2 bg-amber-50 text-amber-600 rounded-lg font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Copy size={18} />
                  Clone to New Date
                </button>
                <button
                  onClick={() => {
                    setShowEditForm(true);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit3 size={18} />
                  Edit Event
                </button>
              </div>
              <button
                onClick={() => {
                  setShowDeleteConfirm(true);
                }}
                className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Delete Event
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 md:p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2">
                Delete Event
              </h3>
              <p className="text-xs md:text-sm text-slate-600 mb-4 md:mb-6">
                Are you sure you want to delete "{selectedEvent.name}"? This action cannot be undone.
              </p>
              <div className="flex gap-2 md:gap-3">
                <button
                  onClick={() => {
                    deleteEvent(selectedEvent.id);
                    setShowDeleteConfirm(false);
                    setSelectedEvent(null);
                  }}
                  className="flex-1 px-3 md:px-4 py-2 md:py-2.5 min-h-[44px] md:min-h-auto bg-red-600 text-white text-sm md:text-base rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-3 md:px-4 py-2 md:py-2.5 min-h-[44px] md:min-h-auto bg-slate-100 text-slate-700 text-sm md:text-base rounded-lg font-medium hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Edit Event Form */}
        {showEditForm && (
          <EditEventForm
            event={selectedEvent}
            onClose={() => setShowEditForm(false)}
            onSave={() => {
              setShowEditForm(false);
            }}
            onViewFullDetails={(evt) => {
              setShowEditForm(false);
              setSelectedEvent(null);
              onSelectEvent?.(evt);
            }}
          />
        )}
      </div>
    );
  };

  // Edit Event Form Component
  const EditEventForm = ({ event, onClose, onSave, onViewFullDetails }) => {
    const [formData, setFormData] = useState({
      name: event.name || '',
      client: event.client || event.clientName || '',
      clientPhone: event.clientPhone || '',
      clientEmail: event.clientEmail || '',
      type: event.type || 'wedding',
      subtype: event.subtype || '',
      guests: event.guests || '',
      date: event.date || '',
      endDate: event.endDate || '',
      venue_id: event.venue_id || '',
      startTime: event.startTime || '',
      endTime: event.endTime || '22:00',
      setupDescription: event.setupDescription || '',
      roomHire: event.roomHire || '',
      billingType: event.billingType || 'group',
      notes: event.notes || '',
    });

    const handleSubmit = async (e) => {
      e.preventDefault();

      const detailBlock = getDetailSaveBlock({ ...event, ...formData }, event);
      if (detailBlock) {
        notifyError(detailBlock);
        return;
      }

      // Same closure guardrail as the add-event flow above, but only when
      // the venue or date actually changed — an edit that leaves placement
      // untouched (e.g. updating guest count on an event booked before the
      // closure existed) must not suddenly get blocked by a closure added
      // afterwards.
      const placementChanged =
        event.venue_id !== formData.venue_id
        || event.date !== formData.date
        || (event.endDate || '') !== (formData.endDate || '');
      if (placementChanged) {
        try {
          const closureDates = [formData.date, formData.endDate].filter(Boolean);
          const closureBlocks = await fetchVenueBlocksByDates(closureDates);
          const closureReason = getVenueClosureReason(formData.venue_id, formData.date, formData.endDate, closureBlocks);
          if (closureReason) {
            notifyError(`This venue is closed for the selected date(s): ${closureReason}. Choose a different date or venue.`);
            return;
          }
        } catch (err) {
          console.error('[RoomDiary] Could not verify venue closures:', err);
        }
      }

      const updates = {
        name: formData.name,
        client: formData.client,
        clientPhone: formData.clientPhone,
        clientEmail: formData.clientEmail,
        type: formData.type,
        subtype: formData.subtype,
        guests: parseInt(formData.guests) || 0,
        date: formData.date,
        endDate: formData.endDate || null,
        venue_id: formData.venue_id,
        startTime: formData.startTime,
        endTime: formData.endTime,
        setupDescription: formData.setupDescription,
        roomHire: parseFloat(formData.roomHire) || 0,
        billingType: formData.billingType,
        notes: formData.notes,
      };

      await updateEvent(event.id, updates);

      // Auto-create client if not linked to existing one
      const existingClient = clients?.find((c) => c.name.toLowerCase() === formData.client.toLowerCase());
      if (!existingClient && formData.client) {
        const typeToTag = {
          corporate: 'corporate',
          community: 'community',
          charity: 'charity',
          private_party: 'private',
          wedding: 'wedding',
          music_night: 'other',
          restaurant_booking: 'other',
          social: 'other',
          other: 'other',
        };
        await addClient({
          name: formData.client,
          phone: formData.clientPhone || '',
          email: formData.clientEmail || '',
          tag: typeToTag[formData.type] || 'other',
        });
      }

      setSelectedEvent({ ...event, ...updates });
      onSave();
    };

    const displayVenues = venuesForForms;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 md:p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 p-3 md:p-6 flex items-center justify-between gap-2">
            <h2 className="text-lg md:text-2xl font-bold text-slate-900">Edit Event</h2>
            <button
              onClick={onClose}
              className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center flex-shrink-0"
            >
              <X size={18} className="md:size-5 text-slate-600" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-3 md:p-6 space-y-3 md:space-y-4">
            {/* Event Name */}
            <div>
              <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1">
                Event Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 md:py-2.5 text-sm md:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] md:min-h-auto"
                placeholder="e.g., Sarah's Wedding"
              />
            </div>

            {/* Client Information */}
            <div className="space-y-2 md:space-y-3">
              <h3 className="font-semibold text-sm md:text-base text-slate-900">Client Information</h3>

              <div className="relative">
                <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 md:py-2.5 text-sm md:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] md:min-h-auto"
                  value={formData.client}
                  onChange={(e) => handleClientNameChange(e, setFormData)}
                  onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                  autoComplete="off"
                  placeholder="John Doe"
                />
                {showClientSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {clientSuggestions.map((client) => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => handleSelectClient(client, setFormData)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                      >
                        <span className="font-medium">{client.name}</span>
                        {client.company && <span className="text-slate-400 ml-2">{client.company}</span>}
                        {client.tag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">{client.tag}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.clientPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, clientPhone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+353 1 234 5678"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, clientEmail: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Date and Venue (Move Event) */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Move size={16} />
                Move Event (Change Date/Venue)
              </h3>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                    min={formData.date || undefined}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Venue
                </label>
                <select
                  required
                  value={formData.venue_id}
                  onChange={(e) =>
                    setFormData({ ...formData, venue_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a venue</option>
                  {displayVenues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Event Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Event Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="wedding">Wedding</option>
                  <option value="corporate">Corporate</option>
                  <option value="private_party">Private Party</option>
                  <option value="social">Social</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Subtype
                </label>
                <input
                  type="text"
                  value={formData.subtype}
                  onChange={(e) =>
                    setFormData({ ...formData, subtype: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Reception"
                />
              </div>
            </div>

            {/* Guests & Times */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Guest Count
                </label>
                <input
                  type="number"
                  value={formData.guests}
                  onChange={(e) =>
                    setFormData({ ...formData, guests: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Start Time
                </label>
                <TimeSelect
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  End Time
                </label>
                <TimeSelect
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Setup Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Setup Description
              </label>
              <textarea
                value={formData.setupDescription}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    setupDescription: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any special setup requirements..."
                rows="3"
              />
            </div>

            {/* Billing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Room Hire
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.roomHire}
                  onChange={(e) =>
                    setFormData({ ...formData, roomHire: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Billing Type
                </label>
                <select
                  value={formData.billingType}
                  onChange={(e) =>
                    setFormData({ ...formData, billingType: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="group">Group</option>
                  <option value="individual">Individual</option>
                  <option value="split">Split</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional notes..."
                rows="3"
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
              {onViewFullDetails && (
                <button
                  type="button"
                  onClick={() => onViewFullDetails(event)}
                  className="w-full px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg font-medium hover:bg-emerald-100 transition-colors text-sm"
                >
                  Edit Full Details in Events Page
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Initialize add form when showAddForm changes or clone source changes
  // Skip reset when editing — handleOpenEditModal already set the data
  useEffect(() => {
    if (showAddForm && !editingEventId) {
      if (cloneSourceEvent) {
        setAddFormFields({
          ...getEmptyAddFormData(),
          eventName: (cloneSourceEvent.name || '') + ' (Copy)',
          clientName: cloneSourceEvent.client || cloneSourceEvent.clientName || '',
          contactPhone: cloneSourceEvent.clientPhone || '',
          contactEmail: cloneSourceEvent.clientEmail || '',
          type: cloneSourceEvent.type || 'private_party',
          subtype: cloneSourceEvent.subtype || '',
          guests: cloneSourceEvent.guests || 0,
          startTime: cloneSourceEvent.startTime || '18:00',
          endTime: cloneSourceEvent.endTime || '22:00',
          setup: cloneSourceEvent.setup || 'banquet',
          setupRequirements: cloneSourceEvent.setupRequirements || '',
          menuNotes: cloneSourceEvent.menuNotes || '',
          equipment: cloneSourceEvent.equipment || [],
          fbItems: cloneSourceEvent.fbItems || [],
          services: (cloneSourceEvent.services && cloneSourceEvent.services.length > 0) ? cloneSourceEvent.services : [{ time: '', description: '', location: '' }],
          roomHire: cloneSourceEvent.roomHire || 0,
          pricePerHead: cloneSourceEvent.pricePerHead || 0,
          packageTotal: cloneSourceEvent.packageTotal || 0,
          billingType: cloneSourceEvent.billingType || 'group',
          notes: cloneSourceEvent.notes || '',
        });
      } else {
        setAddFormFields(getEmptyAddFormData());
      }

    }
  }, [showAddForm, cloneSourceEvent]);

  function handleAddFormChange(e) {
    const { name, value, type } = e.target;
    setAddFormFields((prev) => ({
      ...prev,
      [name]: type === 'number' ? (parseFloat(value) || 0) : value,
    }));
  }

  async function handleAddFormSubmit() {
    if (!addFormFields.eventName || !addFormFields.clientName) {
      notifyError('Please fill in at least Event Name and Client Name');
      return;
    }
    // Bookings within 7 days must be complete before they can be saved.
    const detailBlock = getDetailSaveBlock({
      ...addFormFields,
      date: addFormData.date ? toLocalDateKey(addFormData.date) : '',
      venue_id: addFormData.venueId,
    }, null);
    if (detailBlock) {
      notifyError(detailBlock);
      return;
    }

    // Block saving onto a venue that's closed for the date (hotel shut for
    // Christmas, the January off-season, etc.) — same guardrail the
    // restaurant table-booking side already has. Fetched fresh right before
    // saving rather than cached, so a closure added moments ago is caught.
    {
      const addDateKey = toLocalDateKey(addFormData.date);
      const closureDates = [addDateKey, addFormFields.endDate].filter(Boolean);
      try {
        const closureBlocks = await fetchVenueBlocksByDates(closureDates);
        const closureReason = getVenueClosureReason(addFormData.venueId, addDateKey, addFormFields.endDate, closureBlocks);
        if (closureReason) {
          notifyError(`This venue is closed for the selected date(s): ${closureReason}. Choose a different date or venue.`);
          return;
        }
      } catch (err) {
        // Non-fatal: don't block a legitimate booking over a network hiccup
        // checking closures — just log it and let the save proceed.
        console.error('[RoomDiary] Could not verify venue closures:', err);
      }
    }

    const eventData = {
      name: addFormFields.eventName,
      client: addFormFields.clientName,
      clientPhone: addFormFields.contactPhone,
      clientEmail: addFormFields.contactEmail,
      type: addFormFields.type,
      subtype: addFormFields.subtype,
      guests: parseInt(addFormFields.guests) || 0,
      date: toLocalDateKey(addFormData.date),
      endDate: addFormFields.endDate || null,
      venue_id: addFormData.venueId,
      startTime: addFormFields.startTime,
      endTime: addFormFields.endTime,
      setup: addFormFields.setup,
      setupRequirements: addFormFields.setupRequirements,
      menuNotes: addFormFields.menuNotes,
      dietaryRequirements: addFormFields.dietaryRequirements,
      dietaryVeg: addFormFields.dietaryVeg ?? '',
      dietaryVegan: addFormFields.dietaryVegan ?? '',
      dietaryCoeliac: addFormFields.dietaryCoeliac ?? '',
      dietaryAllergens: addFormFields.dietaryAllergens ?? '',
      dietaryNA: !!addFormFields.dietaryNA,
      musicOnly: !!addFormFields.musicOnly,
      foodServiceTime: addFormFields.foodServiceTime ?? '',
      equipment: addFormFields.equipment,
      fbItems: addFormFields.fbItems,
      services: addFormFields.services,
      roomHire: parseFloat(addFormFields.roomHire) || 0,
      pricePerHead: parseFloat(addFormFields.pricePerHead) || 0,
      packageTotal: parseFloat(addFormFields.packageTotal) || 0,
      billingType: addFormFields.billingType,
      stage: addFormFields.stage,
      notes: addFormFields.notes,
    };

    try {
      await addEvent(eventData);
    } catch (err) {
      notifyError(`Failed to save event: ${err.message}`);
      return;
    }

    // Auto-create client
    const existingClient = clients?.find((c) => c.name.toLowerCase() === addFormFields.clientName.toLowerCase());
    if (!existingClient && addFormFields.clientName) {
      const typeToTag = {
        corporate: 'corporate', community: 'community', charity: 'charity',
        private_party: 'private', wedding: 'wedding', music_night: 'other',
        restaurant_booking: 'other', social: 'other', other: 'other',
      };
      await addClient({
        name: addFormFields.clientName,
        phone: addFormFields.contactPhone || '',
        email: addFormFields.contactEmail || '',
        tag: typeToTag[addFormFields.type] || 'other',
      });
    }

    setShowAddForm(false);
    setEditingEventId(null);
    setAddFormData({ date: null, venueId: null });
    setCloneSourceEvent(null);
    setAddFormFields(getEmptyAddFormData());
  }

  // Open the unified modal in edit mode for an existing event
  function handleOpenEditModal(event) {
    setEditingEventId(event.id);
    setAddFormFields({
      eventName: event.name || '',
      clientName: event.client || event.clientName || '',
      contactPhone: event.clientPhone || '',
      contactEmail: event.clientEmail || '',
      type: event.type || 'private_party',
      subtype: event.subtype || '',
      guests: event.guests || 0,
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      setup: event.setup || (event.setupDescription ? 'other' : ''),
      setupRequirements: event.setupRequirements || event.setupDescription || '',
      menuNotes: event.menuNotes || '',
      dietaryRequirements: event.dietaryRequirements || '',
      dietaryVeg: event.dietaryVeg ?? '',
      dietaryVegan: event.dietaryVegan ?? '',
      dietaryCoeliac: event.dietaryCoeliac ?? '',
      dietaryAllergens: event.dietaryAllergens ?? '',
      dietaryNA: !!event.dietaryNA,
      musicOnly: !!event.musicOnly,
      foodServiceTime: event.foodServiceTime ?? '',
      equipment: event.equipment || event.equipmentList || [],
      fbItems: event.fbItems || [],
      services: (event.services && event.services.length > 0) ? event.services : [{ time: '', description: '', location: '' }],
      roomHire: event.roomHire || 0,
      pricePerHead: event.pricePerHead || 0,
      packageTotal: event.packageTotal || 0,
      billingType: event.billingType || 'group',
      stage: event.stage || 'enquiry',
      notes: event.notes || '',
      // Edit-only fields
      date: event.date || '',
      endDate: event.endDate || '',
      venue_id: event.venue_id || '',
    });
    setAddFormData({ date: new Date(event.date), venueId: event.venue_id });
    setShowAddForm(true);
    setSelectedEvent(null);
    setShowEditForm(false);
  }

  // Save edits to an existing event
  async function handleEditFormSubmit() {
    if (!addFormFields.eventName || !addFormFields.clientName) {
      notifyError('Please fill in at least Event Name and Client Name');
      return;
    }
    {
      // Moving an incomplete event into the 7-day window counts as a late
      // booking; events already inside the window may save partially.
      const original = events.find((e) => e.id === editingEventId) || null;
      const detailBlock = getDetailSaveBlock({
        ...addFormFields,
        date: addFormFields.date || (addFormData.date ? toLocalDateKey(addFormData.date) : ''),
        venue_id: addFormFields.venue_id || addFormData.venueId,
      }, original);
      if (detailBlock) {
        notifyError(detailBlock);
        return;
      }
    }
    const updates = {
      name: addFormFields.eventName,
      client: addFormFields.clientName,
      clientPhone: addFormFields.contactPhone,
      clientEmail: addFormFields.contactEmail,
      type: addFormFields.type,
      subtype: addFormFields.subtype,
      guests: parseInt(addFormFields.guests) || 0,
      date: addFormFields.date || (addFormData.date ? toLocalDateKey(addFormData.date) : ''),
      endDate: addFormFields.endDate || null,
      venue_id: addFormFields.venue_id || addFormData.venueId,
      startTime: addFormFields.startTime,
      endTime: addFormFields.endTime,
      setup: addFormFields.setup,
      setupRequirements: addFormFields.setupRequirements,
      menuNotes: addFormFields.menuNotes,
      dietaryRequirements: addFormFields.dietaryRequirements,
      dietaryVeg: addFormFields.dietaryVeg ?? '',
      dietaryVegan: addFormFields.dietaryVegan ?? '',
      dietaryCoeliac: addFormFields.dietaryCoeliac ?? '',
      dietaryAllergens: addFormFields.dietaryAllergens ?? '',
      dietaryNA: !!addFormFields.dietaryNA,
      musicOnly: !!addFormFields.musicOnly,
      foodServiceTime: addFormFields.foodServiceTime ?? '',
      equipment: addFormFields.equipment,
      fbItems: addFormFields.fbItems,
      services: addFormFields.services,
      roomHire: parseFloat(addFormFields.roomHire) || 0,
      pricePerHead: parseFloat(addFormFields.pricePerHead) || 0,
      packageTotal: parseFloat(addFormFields.packageTotal) || 0,
      billingType: addFormFields.billingType,
      stage: addFormFields.stage,
      notes: addFormFields.notes,
    };

    try {
      await updateEvent(editingEventId, updates);
    } catch (err) {
      notifyError(`Failed to save changes: ${err.message}`);
      return;
    }

    // Auto-create client
    const existingClient = clients?.find((c) => c.name.toLowerCase() === addFormFields.clientName.toLowerCase());
    if (!existingClient && addFormFields.clientName) {
      const typeToTag = {
        corporate: 'corporate', community: 'community', charity: 'charity',
        private_party: 'private', wedding: 'wedding', music_night: 'other',
        restaurant_booking: 'other', social: 'other', other: 'other',
      };
      await addClient({
        name: addFormFields.clientName,
        phone: addFormFields.contactPhone || '',
        email: addFormFields.contactEmail || '',
        tag: typeToTag[addFormFields.type] || 'other',
      });
    }

    setShowAddForm(false);
    setEditingEventId(null);
    setAddFormData({ date: null, venueId: null });
    setAddFormFields(getEmptyAddFormData());
  }

  // Delete a single payment (e.g. mistyped amount, wrong event, etc.)
  async function handleDeletePayment(payment) {
    if (!payment || !payment.id) return;
    const label = payment.description
      ? `"${payment.description}" (${fmt(payment.amount)})`
      : `${fmt(payment.amount)} on ${fmtDate(payment.payment_date)}`;
    if (!window.confirm(`Delete payment ${label}?\n\nThis cannot be undone.`)) return;
    try {
      await deletePayment(payment.id);
      notifySuccess('Payment deleted');
    } catch (err) {
      notifyError('Failed to delete payment: ' + (err?.message || 'unknown error'));
    }
  }

  // Payment handler
  async function handleRecordPayment(eventId) {
    const amount = parseFloat(paymentFormData.amount);
    if (!amount || amount <= 0) return;
    try {
      const saved = await addPayment({
        eventId, amount,
        payment_date: paymentFormData.payment_date,
        paymentType: paymentFormData.paymentType,
        category: paymentFormData.category,
        type: 'Payment',
        description: paymentFormData.description,
      });
      if (saved && typeof saved.id === 'string' && saved.id.startsWith('temp_')) {
        notifyError('Payment was not saved to the database. Check your connection and try again.');
        return;
      }
      notifySuccess('Payment recorded');
    } catch (err) {
      notifyError('Failed to save payment: ' + (err?.message || 'unknown error'));
      return;
    }
    setPaymentFormData({ amount: '', payment_date: todayKey(), paymentType: 'bank_transfer', category: 'fb_package', description: '' });
    setShowPaymentForm(false);
  }

  // Attachment handlers
  async function handleFileUpload(eventId, files) {
    if (!files || files.length === 0) return;
    setUploadingAttachment(true);
    try {
      const event = events.find(e => e.id === eventId);
      const existingAttachments = event?.attachments || [];
      const newAttachments = [];
      for (const file of files) {
        const meta = await uploadAttachment(eventId, file);
        newAttachments.push(meta);
      }
      const allAttachments = [...existingAttachments, ...newAttachments];
      updateEvent(eventId, { attachments: allAttachments });
    } catch (err) {
      console.error('Upload failed:', err);
      notifyError('Failed to upload file. Please try again.');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteAttachment(eventId, attachment) {
    if (!window.confirm(`Delete "${attachment.name}"?`)) return;
    try {
      await deleteAttachment(attachment.path);
      const event = events.find(e => e.id === eventId);
      const updated = (event?.attachments || []).filter(a => a.id !== attachment.id);
      updateEvent(eventId, { attachments: updated });
    } catch (err) {
      console.error('Delete failed:', err);
      notifyError('Failed to delete file.');
    }
  }

  async function handleDownloadAttachment(attachment) {
    try {
      const url = await getAttachmentUrl(attachment.path);
      window.open(url, '_blank');
    } catch (err) {
      window.open(attachment.url, '_blank');
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // ---- Render the Unified Event Form (inline, not a nested component) ----
  function renderAddEventForm() {
    const isEditMode = !!editingEventId;
    const formData = addFormFields;
    // Red box around required fields still empty.
    const missR = missingFieldClass(isEditMode ? formData : { ...formData, date: addFormData.date ? toLocalDateKey(addFormData.date) : '', venue_id: addFormData.venueId });
    const setField = (field, value) => setAddFormFields(prev => ({ ...prev, [field]: value }));
    const recentNonWeddingEvents = events
      .filter((evt) => evt.type !== 'wedding')
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 20);

    const venue = venues.find((v) => v.id === (isEditMode ? formData.venue_id : addFormData.venueId));
    const displayVenues = venuesForForms;
    const closeForm = () => { setShowAddForm(false); setEditingEventId(null); setAddFormData({ date: null, venueId: null }); setCloneSourceEvent(null); setAddFormFields(getEmptyAddFormData()); };
    const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";
    const labelCls = "block text-xs font-medium text-slate-500 mb-1";

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-slate-900">{isEditMode ? 'Edit Event' : 'Add Event'}</h2>
              <p className="text-sm text-slate-500">{venue?.name || 'Unknown Venue'} — {isEditMode ? fmtDateRange(formData.date, formData.endDate) : addFormData.date ? fmtDate(addFormData.date) : ''}</p>
            </div>
            <button onClick={closeForm} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X size={20} className="text-slate-600" />
            </button>
          </div>

          {/* Clone from Existing — Add Mode Only */}
          {!isEditMode && recentNonWeddingEvents.length > 0 && (
            <div className="px-6 pt-4 pb-2">
              <label className={labelCls}>Clone from Existing Event (Optional)</label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    const sel = events.find((evt) => evt.id === e.target.value);
                    if (sel) {
                      setAddFormFields({
                        ...getEmptyAddFormData(),
                        eventName: sel.name + ' (Copy)',
                        clientName: sel.client || sel.clientName || '',
                        contactPhone: sel.clientPhone || '',
                        contactEmail: sel.clientEmail || '',
                        type: sel.type || 'private_party',
                        subtype: sel.subtype || '',
                        guests: sel.guests || 0,
                        startTime: sel.startTime || '18:00',
                        endTime: sel.endTime || '22:00',
                        setup: sel.setup || 'banquet',
                        setupRequirements: sel.setupRequirements || '',
                        menuNotes: sel.menuNotes || '',
                        equipment: sel.equipment || [],
                        fbItems: sel.fbItems || [],
                        services: sel.services || [],
                        roomHire: sel.roomHire || 0,
                        pricePerHead: sel.pricePerHead || 0,
                        packageTotal: sel.packageTotal || 0,
                        billingType: sel.billingType || 'group',
                        stage: sel.stage || 'enquiry',
                        notes: sel.notes || '',
                      });
                      e.target.value = '';
                    }
                  }
                }}
                className={inputCls}
                defaultValue=""
              >
                <option value="">-- Select an event to clone --</option>
                {recentNonWeddingEvents.map((evt) => (
                  <option key={evt.id} value={evt.id}>{evt.name} ({evt.type}) — {fmtDate(new Date(evt.date))}</option>
                ))}
              </select>
            </div>
          )}

          {/* Form Content — Single Scrollable Page */}
          <div className={isEditMode ? 'p-6 space-y-6' : 'p-6 space-y-6'}>
            {/* ===== EDIT MODE ===== */}
            {isEditMode && (
              <>
                {/* Section 1: Event Info */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Event Information</h4>
                  <MusicOnlyToggle data={formData} onChange={handleAddFormChange} className="mb-4" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Event Name *</label>
                        <input name="eventName" value={formData.eventName} onChange={handleAddFormChange} className={inputCls + missR('name')} placeholder="e.g., Conference Dinner" />
                      </div>
                      <div className="relative">
                        <label className={labelCls}>Client Name *</label>
                        <input
                          value={formData.clientName}
                          onChange={(e) => {
                            setField('clientName', e.target.value);
                            if (e.target.value.length >= 2 && clients) {
                              const matches = clients.filter((c) => c.name.toLowerCase().includes(e.target.value.toLowerCase())).slice(0, 5);
                              setClientSuggestions(matches);
                              setShowClientSuggestions(matches.length > 0);
                            } else { setShowClientSuggestions(false); }
                          }}
                          onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                          autoComplete="off"
                          className={inputCls + missR('client')}
                          placeholder="John Doe"
                        />
                        {showClientSuggestions && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {clientSuggestions.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setAddFormFields(prev => ({
                                    ...prev,
                                    clientName: client.name,
                                    contactPhone: client.phone || prev.contactPhone,
                                    contactEmail: client.email || prev.contactEmail,
                                  }));
                                  setShowClientSuggestions(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm"
                              >
                                <span className="font-medium">{client.name}</span>
                                {client.company && <span className="text-slate-400 ml-2">{client.company}</span>}
                                {client.tag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">{client.tag}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>Phone</label>
                        <input name="contactPhone" value={formData.contactPhone} onChange={handleAddFormChange} className={inputCls + missR('contact')} />
                      </div>
                      <div>
                        <label className={labelCls}>Email</label>
                        <input name="contactEmail" value={formData.contactEmail} onChange={handleAddFormChange} className={inputCls + missR('contact')} />
                      </div>
                      <div>
                        <label className={labelCls}>Notes</label>
                        <textarea name="notes" value={formData.notes} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Guests</label>
                        <input type="number" name="guests" value={formData.guests} onChange={handleAddFormChange} className={inputCls + missR('guests')} />
                      </div>
                      <div>
                        <label className={labelCls}>Price Per Head (€)</label>
                        <input type="number" name="pricePerHead" value={formData.pricePerHead || ''} onChange={(e) => {
                          const pph = parseFloat(e.target.value) || 0;
                          const autoTotal = pph * (formData.guests || 0);
                          setAddFormFields(prev => ({ ...prev, pricePerHead: pph, packageTotal: autoTotal }));
                        }} className={inputCls + missR('pricing')} />
                        {formData.pricePerHead > 0 && formData.guests > 0 && (
                          <p className="text-xs text-slate-400 mt-1">{fmt(formData.pricePerHead)} × {formData.guests} guests = {fmt((formData.pricePerHead || 0) * (formData.guests || 0))}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Start Time</label>
                          <TimeSelect name="startTime" value={formData.startTime} onChange={handleAddFormChange} className={inputCls + missR('startTime')} />
                        </div>
                        <div>
                          <label className={labelCls}>End Time</label>
                          <TimeSelect name="endTime" value={formData.endTime} onChange={handleAddFormChange} className={inputCls + missR('endTime')} />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Stage</label>
                        <select name="stage" value={formData.stage} onChange={handleAddFormChange} className={inputCls}>
                          {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Move Event Box */}
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-blue-700 flex items-center gap-1"><Move size={12} /> Move Event</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className={labelCls}>Start Date</label>
                        <input type="date" name="date" value={formData.date} onChange={handleAddFormChange} className={inputCls + missR('date')} />
                      </div>
                      <div>
                        <label className={labelCls}>End Date</label>
                        <input type="date" name="endDate" value={formData.endDate || ''} onChange={handleAddFormChange} min={formData.date || undefined} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Venue</label>
                        <select name="venue_id" value={formData.venue_id} onChange={handleAddFormChange} className={inputCls + missR('venue')}>
                          <option value="">Select venue</option>
                          {displayVenues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Room Setup */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Room Setup</h4>
                  <div className="space-y-4">
                    <div>
                      <label className={labelCls}>Setup Style *</label>
                      <select name="setup" value={formData.setup} onChange={handleAddFormChange} className={inputCls + missR('setup')}>
                        <option value="">Select setup</option>
                        {SETUP_STYLES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls + ' mb-2'}>Equipment</label>
                      <div className="flex flex-wrap gap-2">
                        {EQUIPMENT_OPTIONS.map((eq) => (
                          <button
                            key={eq}
                            type="button"
                            onClick={() => {
                              const updated = formData.equipment.includes(eq)
                                ? formData.equipment.filter((e) => e !== eq)
                                : [...formData.equipment, eq];
                              setField('equipment', updated);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                              formData.equipment.includes(eq)
                                ? 'bg-emerald-600 text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {eq.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Setup Requirements *</label>
                      <textarea name="setupRequirements" placeholder="Table setup: no. of tables, seats per table, top table, layout, linen &amp; extras (e.g. 8 rounds of 10, top table for 6, white linen, cake table)" value={formData.setupRequirements} onChange={handleAddFormChange} rows={3} className={inputCls + ' resize-none' + missR('setupRequirements')} />
                    </div>
                  </div>
                </div>

                {/* Section 3: Services */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-900">Services</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newServices = [...(formData.services || []), { time: '', description: '', location: '' }];
                        setField('services', newServices);
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition"
                    >
                      <Plus size={12} /> Add Service
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(formData.services || []).map((service, idx) => (
                      <div key={idx} className="flex gap-2 items-end">
                        <div className="w-24">
                          <label className={labelCls + ' text-[10px]'}>Time</label>
                          <TimeSelect
                            value={service.time || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].time = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                          />
                        </div>
                        <div className="flex-1">
                          <label className={labelCls + ' text-[10px]'}>Description</label>
                          <input
                            type="text"
                            value={service.description || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].description = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                            placeholder="e.g., Tea, Coffee & Scones"
                          />
                        </div>
                        <div className="w-32">
                          <label className={labelCls + ' text-[10px]'}>Location</label>
                          <input
                            type="text"
                            value={service.location || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].location = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                            placeholder="e.g., In Room"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (formData.services || []).filter((_, i) => i !== idx);
                            setField('services', updated);
                          }}
                          className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className={labelCls}>Dietary Requirements</label>
                      <textarea name="dietaryRequirements" value={formData.dietaryRequirements} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                      <KitchenFields data={formData} onChange={handleAddFormChange} />
                    </div>
                    <div>
                      <label className={labelCls}>Menu Notes</label>
                      <textarea name="menuNotes" value={formData.menuNotes} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                    </div>
                  </div>
                </div>

                {/* Section 4: Tasks */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-900">On the Day</h4>
                    {(() => {
                      const openTaskCount = eventTasks.filter((t) => t.eventId === editingEventId && !t.completed).length;
                      return openTaskCount > 0 && (
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white">{openTaskCount} open</span>
                      );
                    })()}
                  </div>
                  {formData.contactPhone && (
                    <p className="text-xs text-slate-600 mb-3">Contact: {formData.contactPhone}</p>
                  )}
                  <EventTasks eventId={editingEventId} />
                  <EventHistory eventId={editingEventId} />
                </div>

                {/* Section 5: Financials */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Financials</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Package Total (€)</label>
                        <input type="number" name="packageTotal" value={formData.packageTotal} onChange={handleAddFormChange} className={inputCls + missR('pricing')} />
                      </div>
                      <div>
                        <label className={labelCls}>Room Hire (€)</label>
                        <input type="number" name="roomHire" value={formData.roomHire || 0} onChange={handleAddFormChange} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Billing Type</label>
                        <select name="billingType" value={formData.billingType} onChange={handleAddFormChange} className={inputCls}>
                          <option value="group">Group</option>
                          <option value="individual">Individual</option>
                          <option value="per_head">Per Head</option>
                        </select>
                      </div>
                    </div>

                    {/* Financial Summary */}
                    {(() => {
                      const evtPayments = (payments || []).filter((p) => p.event_id === editingEventId);
                      const totalPaid = evtPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                      const totalVal = (parseFloat(formData.packageTotal) || 0) + (parseFloat(formData.roomHire) || 0);
                      const outstanding = totalVal - totalPaid;
                      return (
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 mb-2">Summary</p>
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Total</p>
                              <p className="text-base font-bold text-slate-900">{fmt(totalVal)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Paid</p>
                              <p className="text-base font-bold text-emerald-700">{fmt(totalPaid)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Outstanding</p>
                              <p className={`text-base font-bold ${outstanding > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(outstanding)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Payments */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                          <CreditCard size={16} /> Payments
                        </h5>
                        <button
                          onClick={() => setShowPaymentForm(!showPaymentForm)}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-white transition"
                          style={{ backgroundColor: '#1A5C3A' }}
                        >
                          <Plus size={12} /> Record
                        </button>
                      </div>
                      {showPaymentForm && (
                        <div className="mb-3 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                          <div className="grid grid-cols-2 gap-2">
                            <input type="number" step="0.01" value={paymentFormData.amount} onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })} placeholder="Amount €" className={inputCls} />
                            <input type="date" value={paymentFormData.payment_date} onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })} className={inputCls} />
                            <select value={paymentFormData.paymentType} onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentType: e.target.value })} className={inputCls}>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="card">Card</option>
                              <option value="cash">Cash</option>
                              <option value="cheque">Cheque</option>
                            </select>
                            <select value={paymentFormData.category} onChange={(e) => setPaymentFormData({ ...paymentFormData, category: e.target.value })} className={inputCls}>
                              <option value="deposit">Deposit</option>
                              <option value="room_hire">Room Hire</option>
                              <option value="fb_package">F&B / Package</option>
                              <option value="final_balance">Final Balance</option>
                              <option value="other">Other</option>
                            </select>
                            <input type="text" value={paymentFormData.description} onChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.target.value })} placeholder="Description" className={inputCls + ' col-span-2'} />
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => handleRecordPayment(editingEventId)} className="flex-1 py-2 rounded-lg text-sm font-medium text-white transition" style={{ backgroundColor: '#1A5C3A' }}>Save Payment</button>
                            <button onClick={() => setShowPaymentForm(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 transition">Cancel</button>
                          </div>
                        </div>
                      )}
                      {(() => {
                        const evtPayments = (payments || []).filter((p) => p.event_id === editingEventId);
                        return evtPayments.length > 0 ? (
                          <div className="space-y-1.5">
                            {evtPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)).map((payment) => (
                              <div key={payment.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">{payment.description || 'Payment'}</p>
                                  <p className="text-xs text-slate-500">
                                    {fmtDate(payment.payment_date)} • {(payment.paymentType || 'bank_transfer').replace(/_/g, ' ')}
                                    {payment.category && payment.category !== 'package' && (
                                      <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-medium uppercase">{payment.category.replace(/_/g, ' ')}</span>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-sm font-bold text-emerald-700">{fmt(payment.amount)}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePayment(payment)}
                                    title="Delete this payment (e.g. added by mistake)"
                                    className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                                    aria-label="Delete payment"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic">No payments yet</p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Section 6: Attachments */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Attachments</h4>
                  <div className="space-y-4">
                    <div>
                      <input ref={fileInputRef} type="file" multiple onChange={(e) => handleFileUpload(editingEventId, e.target.files)} className="hidden" />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                      >
                        <Upload size={16} />
                        {uploadingAttachment ? 'Uploading...' : 'Upload File'}
                      </button>
                    </div>
                    {(() => {
                      const currentEvent = events.find(e => e.id === editingEventId);
                      const attachments = currentEvent?.attachments || [];
                      return attachments.length > 0 ? (
                        <div className="space-y-2">
                          {attachments.map((att) => (
                            <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-2 min-w-0">
                                <File size={16} className="text-slate-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">{att.name}</p>
                                  <p className="text-xs text-slate-500">{formatFileSize(att.size)}</p>
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleDownloadAttachment(att)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
                                >
                                  <Download size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteAttachment(editingEventId, att)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 italic">No attachments yet</p>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            {/* ===== ADD MODE ===== */}
            {!isEditMode && (
              <>
                {/* Section 1: Event Info */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Event Information</h4>
                  <MusicOnlyToggle data={formData} onChange={handleAddFormChange} className="mb-4" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Event Name *</label>
                        <input name="eventName" value={formData.eventName} onChange={handleAddFormChange} className={inputCls + missR('name')} placeholder="e.g., Conference Dinner" />
                      </div>
                      <div className="relative">
                        <label className={labelCls}>Client Name *</label>
                        <input
                          value={formData.clientName}
                          onChange={(e) => {
                            setField('clientName', e.target.value);
                            if (e.target.value.length >= 2 && clients) {
                              const matches = clients.filter((c) => c.name.toLowerCase().includes(e.target.value.toLowerCase())).slice(0, 5);
                              setClientSuggestions(matches);
                              setShowClientSuggestions(matches.length > 0);
                            } else { setShowClientSuggestions(false); }
                          }}
                          onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                          autoComplete="off"
                          className={inputCls + missR('client')}
                          placeholder="John Doe"
                        />
                        {showClientSuggestions && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {clientSuggestions.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setAddFormFields(prev => ({
                                    ...prev,
                                    clientName: client.name,
                                    contactPhone: client.phone || prev.contactPhone,
                                    contactEmail: client.email || prev.contactEmail,
                                  }));
                                  setShowClientSuggestions(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm"
                              >
                                <span className="font-medium">{client.name}</span>
                                {client.company && <span className="text-slate-400 ml-2">{client.company}</span>}
                                {client.tag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">{client.tag}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>Phone</label>
                        <input name="contactPhone" value={formData.contactPhone} onChange={handleAddFormChange} className={inputCls + missR('contact')} />
                      </div>
                      <div>
                        <label className={labelCls}>Email</label>
                        <input name="contactEmail" value={formData.contactEmail} onChange={handleAddFormChange} className={inputCls + missR('contact')} />
                      </div>
                      <div>
                        <label className={labelCls}>Notes</label>
                        <textarea name="notes" value={formData.notes} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Guests</label>
                        <input type="number" name="guests" value={formData.guests} onChange={handleAddFormChange} className={inputCls + missR('guests')} />
                      </div>
                      <div>
                        <label className={labelCls}>Price Per Head (€)</label>
                        <input type="number" name="pricePerHead" value={formData.pricePerHead || ''} onChange={(e) => {
                          const pph = parseFloat(e.target.value) || 0;
                          const autoTotal = pph * (formData.guests || 0);
                          setAddFormFields(prev => ({ ...prev, pricePerHead: pph, packageTotal: autoTotal }));
                        }} className={inputCls + missR('pricing')} />
                        {formData.pricePerHead > 0 && formData.guests > 0 && (
                          <p className="text-xs text-slate-400 mt-1">{fmt(formData.pricePerHead)} × {formData.guests} guests = {fmt((formData.pricePerHead || 0) * (formData.guests || 0))}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Start Time</label>
                          <TimeSelect name="startTime" value={formData.startTime} onChange={handleAddFormChange} className={inputCls + missR('startTime')} />
                        </div>
                        <div>
                          <label className={labelCls}>End Time</label>
                          <TimeSelect name="endTime" value={formData.endTime} onChange={handleAddFormChange} className={inputCls + missR('endTime')} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Room Setup */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Room Setup</h4>
                  <div className="space-y-4">
                    <div>
                      <label className={labelCls}>Setup Style *</label>
                      <select name="setup" value={formData.setup} onChange={handleAddFormChange} className={inputCls + missR('setup')}>
                        <option value="">Select setup</option>
                        {SETUP_STYLES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls + ' mb-2'}>Equipment</label>
                      <div className="flex flex-wrap gap-2">
                        {EQUIPMENT_OPTIONS.map((eq) => (
                          <button
                            key={eq}
                            type="button"
                            onClick={() => {
                              const updated = formData.equipment.includes(eq)
                                ? formData.equipment.filter((e) => e !== eq)
                                : [...formData.equipment, eq];
                              setField('equipment', updated);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                              formData.equipment.includes(eq)
                                ? 'bg-emerald-600 text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {eq.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Setup Requirements *</label>
                      <textarea name="setupRequirements" placeholder="Table setup: no. of tables, seats per table, top table, layout, linen &amp; extras (e.g. 8 rounds of 10, top table for 6, white linen, cake table)" value={formData.setupRequirements} onChange={handleAddFormChange} rows={3} className={inputCls + ' resize-none' + missR('setupRequirements')} />
                    </div>
                  </div>
                </div>

                {/* Section 3: Services */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-900">Services</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newServices = [...(formData.services || []), { time: '', description: '', location: '' }];
                        setField('services', newServices);
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition"
                    >
                      <Plus size={12} /> Add Service
                    </button>
                  </div>
                  <div className="space-y-2 mb-4">
                    {(formData.services || []).map((service, idx) => (
                      <div key={idx} className="flex gap-2 items-end">
                        <div className="w-24">
                          <label className={labelCls + ' text-[10px]'}>Time</label>
                          <TimeSelect
                            value={service.time || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].time = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                          />
                        </div>
                        <div className="flex-1">
                          <label className={labelCls + ' text-[10px]'}>Description</label>
                          <input
                            type="text"
                            value={service.description || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].description = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                            placeholder="e.g., Tea, Coffee & Scones"
                          />
                        </div>
                        <div className="w-32">
                          <label className={labelCls + ' text-[10px]'}>Location</label>
                          <input
                            type="text"
                            value={service.location || ''}
                            onChange={(e) => {
                              const updated = [...(formData.services || [])];
                              updated[idx].location = e.target.value;
                              setField('services', updated);
                            }}
                            className={inputCls}
                            placeholder="e.g., In Room"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (formData.services || []).filter((_, i) => i !== idx);
                            setField('services', updated);
                          }}
                          className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className={labelCls}>Dietary Requirements</label>
                      <textarea name="dietaryRequirements" value={formData.dietaryRequirements} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                      <KitchenFields data={formData} onChange={handleAddFormChange} />
                    </div>
                    <div>
                      <label className={labelCls}>Menu Notes</label>
                      <textarea name="menuNotes" value={formData.menuNotes} onChange={handleAddFormChange} rows={2} className={inputCls + ' resize-none'} />
                    </div>
                  </div>
                </div>

                {/* Section 4: Financials */}
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Financials</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Price Per Head (€)</label>
                        <input type="number" name="pricePerHead" value={formData.pricePerHead || ''} onChange={(e) => {
                          const pph = parseFloat(e.target.value) || 0;
                          const autoTotal = pph * (formData.guests || 0);
                          setAddFormFields(prev => ({ ...prev, pricePerHead: pph, packageTotal: autoTotal }));
                        }} className={inputCls + missR('pricing')} />
                      </div>
                      <div>
                        <label className={labelCls}>Package Total (€)</label>
                        <input type="number" name="packageTotal" value={formData.packageTotal} onChange={handleAddFormChange} className={inputCls + missR('pricing')} />
                      </div>
                      <div>
                        <label className={labelCls}>Room Hire (€)</label>
                        <input type="number" name="roomHire" value={formData.roomHire || 0} onChange={handleAddFormChange} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Billing Type</label>
                        <select name="billingType" value={formData.billingType} onChange={handleAddFormChange} className={inputCls}>
                          <option value="group">Group</option>
                          <option value="individual">Individual</option>
                          <option value="per_head">Per Head</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Stage</label>
                      <select name="stage" value={formData.stage} onChange={handleAddFormChange} className={inputCls}>
                        {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Linked Table Bookings (edit mode only) */}
          {isEditMode && (() => {
            const linkedBookings = (tableBookings || []).filter((b) => b.linkedEventId === editingEventId);
            if (linkedBookings.length === 0) return null;
            return (
              <div className="px-6 pb-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2 mb-3">
                    <Link2 size={14} /> Linked Table Bookings ({linkedBookings.length})
                  </h3>
                  <div className="space-y-2">
                    {linkedBookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-purple-100">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{b.guestName}</p>
                          <p className="text-xs text-slate-500">{b.covers} covers • {b.time} • {b.venue}</p>
                        </div>
                        <button
                          onClick={async () => {
                            await unlinkBookingFromEvent(b.id, 'booked');
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                          title="Unlink and restore as active table booking"
                        >
                          <Unlink size={12} /> Revert
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-purple-500 mt-2">These bookings were upgraded from table service. Covers no longer count against restaurant capacity.</p>
                </div>
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4">
            {isEditMode && (
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => { handlePrintEventSheet(editingEventId); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition"
                >
                  <Printer size={13} /> Print Sheet
                </button>
                <button
                  onClick={() => {
                    const evt = events.find(e => e.id === editingEventId);
                    if (evt) {
                      closeForm();
                      setCloneSourceEvent(evt);
                      setAddFormData({ date: null, venueId: null });
                      setShowAddForm(true);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                >
                  <Copy size={13} /> Clone
                </button>
                <button
                  onClick={() => onSelectEvent?.(events.find(e => e.id === editingEventId))}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                >
                  <FileText size={13} /> Events Page
                </button>
                {(() => {
                  const evt = events.find(e => e.id === editingEventId);
                  if (!evt) return null;
                  const linked = getLinkedTableBookingForEvent(evt.id);
                  if (linked) {
                    return (
                      <button
                        onClick={() => { handleUnlinkFromRestaurant(evt); closeForm(); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition"
                        title="Remove the linked table booking and restore the event's room"
                      >
                        <Unlink size={13} /> Remove Restaurant
                      </button>
                    );
                  }
                  if (canSendEventToRestaurant(evt)) {
                    return (
                      <button
                        onClick={() => { closeForm(); handleOpenSendToRestaurant(evt); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition"
                        title="Create a matching table booking and release this room"
                      >
                        <Utensils size={13} /> To Restaurant
                      </button>
                    );
                  }
                  return null;
                })()}
                <button
                  onClick={() => {
                    if (window.confirm('Delete this event? This cannot be undone.')) {
                      deleteEvent(editingEventId);
                      closeForm();
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition ml-auto"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            )}
            <div className="mb-3 max-h-40 overflow-y-auto">
              <DetailStatusPanel
                event={isEditMode ? formData : { ...formData, date: addFormData.date ? toLocalDateKey(addFormData.date) : '', venue_id: addFormData.venueId }}
                isNew={!isEditMode}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={closeForm} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition">Cancel</button>
              <button onClick={isEditMode ? handleEditFormSubmit : handleAddFormSubmit} className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition">
                {isEditMode ? <><Save size={16} /> Save Changes</> : <><Plus size={16} /> Create Event</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ FUNCTION SHEET PREVIEW ============
  const [showFunctionSheet, setShowFunctionSheet] = useState(false);
  const [sheetMode, setSheetMode] = useState('week'); // 'week' or 'event'
  const [sheetEventId, setSheetEventId] = useState(null);
  const sheetEvent = events.find((e) => e.id === sheetEventId);

  const handlePrintWeekSheet = () => {
    setSheetMode('week');
    setShowFunctionSheet(true);
  };

  const handlePrintEventSheet = (eventId) => {
    setSheetEventId(eventId);
    setSheetMode('event');
    setShowFunctionSheet(true);
  };

  // ============ MAIN COMPONENT RETURN ============
  const weekStart = getWeekStart(currentDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const formatShortDate = (date) => date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen space-y-4 print:min-h-0 print:p-0 print:bg-white print:space-y-0">
      {/* Header */}
      <div className="no-print flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Room Diary</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">Function room bookings and availability</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Print Daily Function Sheet — uses the date in the picker below */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="date"
              value={dailySheetDate}
              onChange={(e) => setDailySheetDate(e.target.value)}
              className="border-0 bg-transparent text-xs md:text-sm text-gray-900 focus:outline-none min-h-[36px]"
              title="Select date for daily function sheet"
            />
          </div>
          <button
            onClick={() => {
              if (!dailySheetDate) return;
              onNavigate?.('functionsheets', { action: 'print-daily', date: dailySheetDate });
            }}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-4 py-2 min-h-[44px] rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
            title="Print daily function sheet for the selected date"
          >
            <Printer size={14} className="md:size-4" />
            <span className="hidden sm:inline">Print Daily Sheet</span>
            <span className="sm:hidden">Daily</span>
          </button>
          <button
            onClick={handlePrintWeekSheet}
            className="flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-3 md:px-4 py-2 min-h-[44px] rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
            title="Print function sheet for the week in view"
          >
            <Printer size={14} className="md:size-4" />
            <span className="hidden sm:inline">Print Weekly Sheet</span>
            <span className="sm:hidden">Week</span>
          </button>
        </div>
      </div>

      {/* View Controls */}
      <div className="no-print bg-white rounded-lg shadow p-3 md:p-4 space-y-3 md:space-y-0 md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-2 order-2 md:order-1">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('weekly')}
              className={`px-3 md:px-4 py-1.5 min-h-[44px] md:min-h-auto rounded-md text-xs md:text-sm font-medium transition-colors ${
                viewMode === 'weekly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 md:px-4 py-1.5 min-h-[44px] md:min-h-auto rounded-md text-xs md:text-sm font-medium transition-colors ${
                viewMode === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setViewMode('annual')}
              className={`px-3 md:px-4 py-1.5 min-h-[44px] md:min-h-auto rounded-md text-xs md:text-sm font-medium transition-colors ${
                viewMode === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3 order-1 md:order-2 justify-between md:justify-center flex-wrap">
          <button
            onClick={viewMode === 'weekly' ? handlePrevWeek : viewMode === 'monthly' ? handlePrevMonth : () => {
              const d = new Date(currentDate); d.setFullYear(d.getFullYear() - 1); setCurrentDate(d);
            }}
            className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
          >
            <ChevronLeft size={18} className="md:size-5 text-slate-600" />
          </button>
          <button
            onClick={handleToday}
            className="px-2 md:px-3 py-1.5 min-h-[44px] bg-slate-100 hover:bg-slate-200 rounded-lg text-xs md:text-sm font-medium text-slate-700 transition-colors whitespace-nowrap"
          >
            Today
          </button>
          <span className="text-sm md:text-lg font-semibold text-slate-900 min-w-full md:min-w-[200px] text-center order-3 md:order-none md:flex-shrink-0">
            {viewMode === 'weekly'
              ? `${formatShortDate(weekStart)} — ${formatShortDate(weekEnd)}`
              : viewMode === 'monthly'
              ? currentDate.toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })
              : String(currentDate.getFullYear())}
          </span>
          <button
            onClick={viewMode === 'weekly' ? handleNextWeek : viewMode === 'monthly' ? handleNextMonth : () => {
              const d = new Date(currentDate); d.setFullYear(d.getFullYear() + 1); setCurrentDate(d);
            }}
            className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
          >
            <ChevronRight size={18} className="md:size-5 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Calendar View */}
      <div className="no-print">
        {viewMode === 'weekly' ? <WeeklyView /> : viewMode === 'monthly' ? <MonthlyView /> : <AnnualView />}
      </div>

      {/* Unified Add/Edit Modal */}
      {showAddForm && renderAddEventForm()}

      {/* ============ Function Sheet Preview Modal ============ */}
      {showFunctionSheet && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex flex-col">
          {/* Toolbar */}
          <div className="no-print bg-gray-900 text-white px-3 md:px-6 py-2 md:py-3 flex items-center justify-between shrink-0 gap-2">
            <h2 className="text-sm md:text-lg font-semibold truncate">
              Function Sheet — {sheetMode === 'week' ? 'Weekly' : sheetEvent?.name || ''}
            </h2>
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1 md:gap-2 px-3 md:px-5 py-1.5 md:py-2 min-h-[44px] md:min-h-auto rounded-lg font-medium text-xs md:text-sm text-white"
                style={{ backgroundColor: '#1A5C3A' }}
              >
                <Printer size={14} className="md:size-4" />
                <span className="hidden sm:inline">Print</span>
              </button>
              <button
                onClick={() => setShowFunctionSheet(false)}
                className="p-2 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
              >
                <X size={18} className="md:size-5" />
              </button>
            </div>
          </div>
          {/* Scrollable Sheet */}
          <div className="flex-1 overflow-y-auto bg-gray-200 p-3 md:p-6 print:p-0 print:bg-white">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4 md:p-8 print:shadow-none print:rounded-none print:max-w-none print:p-0">
              <div className="border-b-2 border-gray-300 pb-3 md:pb-4 mb-4 md:mb-6">
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#1A5C3A' }}>Errigal Country House Hotel</h1>
                <h2 className="text-lg md:text-xl text-gray-600 mt-1">Function Sheet</h2>
              </div>

              {sheetMode === 'week' && (() => {
                const ws = getWeekStart(currentDate);
                const days = [];
                for (let i = 0; i < 7; i++) {
                  const d = new Date(ws);
                  d.setDate(d.getDate() + i);
                  days.push(d);
                }
                return (
                  <div>
                    <p className="text-lg font-semibold text-gray-900 mb-6">
                      Week of {fmtDate(ws)} — {fmtDate(days[6])}
                    </p>
                    {days.map((date) => {
                      const dateStr = toLocalDateKey(date);
                      const dayEvents = events
                        .filter((e) => e.date === dateStr)
                        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
                      const dayName = date.toLocaleDateString('en-IE', { weekday: 'long' });
                      return (
                        <div key={dateStr} className="mb-6">
                          <div className="bg-gray-100 px-4 py-2 border-l-4 border-gray-800 mb-3">
                            <h3 className="text-lg font-bold text-gray-900">{dayName}, {fmtDate(date)}</h3>
                            <span className="text-sm text-gray-500">{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</span>
                          </div>
                          {dayEvents.length === 0 ? (
                            <p className="text-sm text-gray-400 italic ml-4">No events</p>
                          ) : (
                            dayEvents.map((evt) => (
                              <div key={evt.id} className="ml-4 mb-4 pb-3 border-b border-gray-200 last:border-b-0">
                                <p className="font-bold text-gray-900 mb-2">{evt.name}</p>
                                <FunctionSheetBody event={evt} venues={venues} compact />
                                <p className="text-xs text-gray-500 mt-2">
                                  <strong>Package:</strong> {fmt(evt.packageTotal || 0)} &nbsp;|&nbsp;
                                  <strong>Room Hire:</strong> {fmt(evt.roomHire || 0)}
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {sheetMode === 'event' && sheetEvent && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{sheetEvent.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {fmtDate(sheetEvent.date)}
                    {sheetEvent.clientPhone ? ` · ${sheetEvent.clientPhone}` : ''}
                    {sheetEvent.clientEmail ? ` · ${sheetEvent.clientEmail}` : ''}
                  </p>
                  <p className="text-sm mb-4">
                    <strong>Package:</strong> {fmt(sheetEvent.packageTotal || 0)} &nbsp;|&nbsp;
                    <strong>Room Hire:</strong> {fmt(sheetEvent.roomHire || 0)} &nbsp;|&nbsp;
                    <strong>Billing:</strong> {sheetEvent.billingType || 'N/A'}
                  </p>
                  <FunctionSheetBody event={sheetEvent} venues={venues} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Send to Restaurant Modal */}
      {showSendToRestaurant && sendToRestaurantEvent && (() => {
        const evt = sendToRestaurantEvent;
        const validSittings = getSittingsForVenueAndDate(sendToRestaurantData.venue, evt.date);
        const currentSittingDef = RESTAURANT_SITTINGS.find((s) => s.id === sendToRestaurantData.sitting);
        const venueCap = RESTAURANT_VENUES.find((v) => v.id === sendToRestaurantData.venue)?.capacity || 60;
        const minCovers = getMinCoversForRestaurant(sendToRestaurantData.venue);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={handleCloseSendToRestaurant}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Utensils size={20} className="text-purple-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Send to Restaurant</h2>
                </div>
                <button onClick={handleCloseSendToRestaurant} className="p-1 hover:bg-slate-100 rounded">
                  <X size={18} className="text-slate-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                  <div className="font-medium text-slate-900">{evt.name}</div>
                  <div className="text-slate-600 mt-1 text-xs">
                    {evt.client} · {fmtDate(evt.date)}{evt.startTime ? ` · ${evt.startTime}` : ''} · {evt.guests || 0} guests
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Restaurant</label>
                  <select
                    value={sendToRestaurantData.venue}
                    onChange={(e) => {
                      const newVenue = e.target.value;
                      const newValid = getSittingsForVenueAndDate(newVenue, evt.date);
                      const newSitting = newValid.some((s) => s.id === sendToRestaurantData.sitting)
                        ? sendToRestaurantData.sitting
                        : (newValid[0]?.id || '');
                      setSendToRestaurantData((prev) => ({ ...prev, venue: newVenue, sitting: newSitting }));
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {RESTAURANT_VENUES.map((v) => (
                      <option key={v.id} value={v.id}>{v.name} (max {v.capacity})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Sitting</label>
                  {validSittings.length === 0 ? (
                    <p className="text-sm text-red-600">This restaurant isn't open on {fmtDate(evt.date)}. Pick a different one.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {RESTAURANT_SITTINGS.map((s) => {
                        const disabled = !validSittings.some((v) => v.id === s.id);
                        const selected = sendToRestaurantData.sitting === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                              const newTime = `${String(s.startH).padStart(2, '0')}:00`;
                              setSendToRestaurantData((prev) => ({ ...prev, sitting: s.id, time: newTime }));
                            }}
                            className={`px-3 py-2 rounded-lg border text-xs font-medium transition ${
                              disabled
                                ? 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'
                                : selected
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-white border-slate-300 text-slate-700 hover:border-purple-400'
                            }`}
                          >
                            <div>{s.label}</div>
                            <div className="text-[10px] opacity-70 mt-0.5">{s.time}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                    <TimeSelect
                      value={sendToRestaurantData.time}
                      onChange={(e) => setSendToRestaurantData((prev) => ({ ...prev, time: e.target.value }))}
                      startHour={currentSittingDef ? currentSittingDef.startH : 6}
                      endHour={currentSittingDef ? currentSittingDef.endH - 1 : 23}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {currentSittingDef && (
                      <p className="text-[10px] text-slate-400 mt-1">Within {currentSittingDef.time}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Covers</label>
                    <input
                      type="number"
                      min={minCovers}
                      max={venueCap}
                      value={sendToRestaurantData.covers}
                      onChange={(e) => setSendToRestaurantData((prev) => ({ ...prev, covers: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      {minCovers > 1 ? `Min ${minCovers} · ` : ''}Max {venueCap}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  A new table booking will be created on the Tables page and linked to this event. The event will leave Room Diary until unlinked.
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
                <button
                  onClick={handleCloseSendToRestaurant}
                  disabled={sendToRestaurantBusy}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSendToRestaurant}
                  disabled={sendToRestaurantBusy || validSittings.length === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <Utensils size={14} />
                  {sendToRestaurantBusy ? 'Sending…' : 'Send to Restaurant'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
